/**
 * Sales Orders API Client
 * Clean API layer - HTTP calls, validation, transformation only
 */

import { getHttpClient, isNumericId, coerceToNumberId, triggerDownload } from '@ufc/core'
import { skusApi } from '@ufc/domains/pricing'

import type {
  SalesOrder,
  SalesOrderStats,
  SalesOrdersFilter,
  SalesOrdersSortBy,
  SalesOrderSkuOption,
  CreateSalesOrderRequest,
  PaginatedSalesOrders,
  PaginationMeta,
} from '../../domain/types'

import {
  SALES_ORDER_ENDPOINTS,
  DEFAULT_PAGE_SIZE,
  DEFAULT_CURRENCY,
  DEFAULT_SALES_ORDER_SORT,
} from '../../domain/constants'

import { calculateUtilization } from '../../presentation/components/sales-orders-section/utils'

import {
  salesOrdersResponseSchema,
  salesOrderResponseSchema,
  salesOrderStatsResponseSchema,
} from '../../api/api-types'

import {
  transformSalesOrderResponseToDomain,
  transformSalesOrdersResponseToDomain,
} from '../../api/transformers'

export interface SalesOrdersExportRequest {
  filter?: SalesOrdersFilter
  email: string
  locale?: string
}

export type SalesOrdersExportResult =
  | { mode: 'download'; count: number; orders: SalesOrder[] }
  | { mode: 'email'; count: number; email: string }

// Fields that can be sorted server-side via API
const API_SORTABLE_FIELDS: Record<string, string> = {
  createdAt: 'createdAt',
  validityEnd: 'validityEnd',
  salesOrderNumber: 'salesOrderNumber',
  status: 'status',
}

// No client-side sort fields are exposed in the UI; all active sort options are server-side.
const CLIENT_SORTABLE_FIELDS: SalesOrdersSortBy[] = []

function isClientSortField(field: SalesOrdersSortBy): boolean {
  return CLIENT_SORTABLE_FIELDS.includes(field)
}

function getApiSortField(field: SalesOrdersSortBy): string | null {
  return API_SORTABLE_FIELDS[field] ?? null
}

type ValueRange = {
  min?: number
  max?: number
}

const STATUS_ORDER: SalesOrdersFilter['status'] = ['active', 'completed', 'draft', 'canceled']

// ============================================
// Query Builders
// ============================================

function normalizeSearchTerm(value?: string): string {
  return value?.trim() ?? ''
}

function splitSearchTerms(value?: string): string[] {
  const normalized = normalizeSearchTerm(value)
  if (!normalized) return []
  // Keep the search as a single term to allow phrase searches like "factory 2"
  return [normalized]
}

function normalizeProductCodes(value?: string[]): string[] {
  if (!value) return []
  return value.map(code => code.trim().toLowerCase()).filter(Boolean)
}

function normalizeStatusFilter(value?: SalesOrdersFilter['status']): Array<'active' | 'completed' | 'draft' | 'canceled'> {
  if (!value || value.length === 0) return []
  const normalized = new Set<'active' | 'completed' | 'draft' | 'canceled'>()
  for (const status of value) {
    if (status) normalized.add(status)
  }
  return STATUS_ORDER
    ? STATUS_ORDER.filter(status => normalized.has(status))
    : Array.from(normalized)
}

function buildApiStatusFilter(statuses: Array<'active' | 'completed' | 'draft' | 'canceled'>): string[] {
  if (statuses.length === 0) return []
  return Array.from(new Set(statuses))
}

function normalizeDeliveryAddresses(value?: string[]): string[] {
  if (!value) return []
  return value.map(addr => addr.trim().toLowerCase()).filter(Boolean)
}

function normalizeFactoryIds(value?: string[]): string[] {
  if (!value) return []
  return value.map(id => id.trim()).filter(Boolean)
}

function normalizeValueRange(min?: number, max?: number): ValueRange {
  const safeMin = Number.isFinite(min) ? (min as number) : undefined
  const safeMax = Number.isFinite(max) ? (max as number) : undefined
  if (safeMin != null && safeMax != null && safeMin > safeMax) {
    return { min: safeMax, max: safeMin }
  }
  return { min: safeMin, max: safeMax }
}

function applySearchFilters(params: URLSearchParams, rawSearch: string) {
  const terms = splitSearchTerms(rawSearch)
  if (terms.length === 0) return

  let index = 0
  for (const term of terms) {
    // Search in salesOrderNumber
    params.set(`filters[$or][${index}][salesOrderNumber][$containsi]`, term)
    index += 1
    // Search in documentId
    params.set(`filters[$or][${index}][documentId][$containsi]`, term)
    index += 1
    // Search in metadata.name.en (English name)
    params.set(`filters[$or][${index}][metadata][name][en][$containsi]`, term)
    index += 1
    // Search in metadata.name.ar (Arabic name)
    params.set(`filters[$or][${index}][metadata][name][ar][$containsi]`, term)
    index += 1
    // Search in deliveryAddress relation name
    params.set(`filters[$or][${index}][deliveryAddress][name][$containsi]`, term)
    index += 1
    // Search in tenant name (for admin views)
    params.set(`filters[$or][${index}][tenant][name][$containsi]`, term)
    index += 1
    // Search in factory name (Strapi i18n uses single field, not nested locale)
    params.set(`filters[$or][${index}][factory][name][$containsi]`, term)
    index += 1
    // Search in factory code
    params.set(`filters[$or][${index}][factory][code][$containsi]`, term)
    index += 1
    // Search by numeric ID
    if (isNumericId(term)) {
      params.set(`filters[$or][${index}][id][$eq]`, term)
      index += 1
    }
  }
}

function matchesProductCodes(order: SalesOrder, productCodes: string[]): boolean {
  if (productCodes.length === 0) return true
  // Support filtering by either variantId, productCode, or productId.
  // `normalizeProductCodes` already lowercases values, but we also add a raw set
  // to be defensive in case any values bypass normalization.
  const lowerSet = new Set(productCodes.map((code) => code.toLowerCase().trim()))
  const rawSet = new Set(productCodes)
  return order.lines.some((line) => {
    // productCode (e.g. CEM-I-42.5N)
    const productCodeRaw = line.productCode?.toString().trim() ?? ''
    const productCodeLc = productCodeRaw.toLowerCase()
    if (productCodeRaw && (lowerSet.has(productCodeLc) || rawSet.has(productCodeRaw))) return true

    // variantId (usually the primary key for SKUs)
    const variantIdRaw = line.variantId != null ? String(line.variantId).trim() : ''
    const variantIdLc = variantIdRaw.toLowerCase()
    if (variantIdRaw && (lowerSet.has(variantIdLc) || rawSet.has(variantIdRaw))) return true

    // productId as a final fallback
    const productIdRaw = line.productId != null ? String(line.productId).trim() : ''
    const productIdLc = productIdRaw.toLowerCase()
    if (productIdRaw && (lowerSet.has(productIdLc) || rawSet.has(productIdRaw))) return true

    return false
  })
}

function matchesDeliveryAddress(order: SalesOrder, deliveryAddresses: string[]): boolean {
  if (deliveryAddresses.length === 0) return true
  const value = (order.deliveryAddressId ?? order.deliveryAddress ?? '').toLowerCase().trim()
  if (!value) return false
  const matchSet = new Set(deliveryAddresses)
  return matchSet.has(value)
}

function matchesFactoryId(order: SalesOrder, factoryIds: string[]): boolean {
  if (factoryIds.length === 0) return true
  const orderFactoryId = order.factoryId?.trim() ?? ''
  if (!orderFactoryId) return false
  const matchSet = new Set(factoryIds)
  return matchSet.has(orderFactoryId)
}

function matchesStatusFilter(order: SalesOrder, statuses: Array<'active' | 'completed' | 'draft' | 'canceled'>): boolean {
  if (statuses.length === 0) return true
  return statuses.includes(order.status)
}

function matchesValueRange(amount: number, range: ValueRange): boolean {
  if (range.min != null && amount < range.min) return false
  if (range.max != null && amount > range.max) return false
  return true
}

type DateRange = {
  from?: string
  to?: string
}

function normalizeDateRange(range?: { from: string; to: string }): DateRange {
  if (!range) return {}
  return {
    from: range.from || undefined,
    to: range.to || undefined,
  }
}

function matchesDateRange(dateStr: string | undefined, range: DateRange): boolean {
  if (!range.from && !range.to) return true
  if (!dateStr) return false

  const date = new Date(dateStr)
  if (Number.isNaN(date.getTime())) return false

  // Normalize to start of day for comparison
  const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate())

  if (range.from) {
    const fromDate = new Date(range.from)
    const fromDateOnly = new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate())
    if (dateOnly < fromDateOnly) return false
  }

  if (range.to) {
    const toDate = new Date(range.to)
    const toDateOnly = new Date(toDate.getFullYear(), toDate.getMonth(), toDate.getDate())
    if (dateOnly > toDateOnly) return false
  }

  return true
}

function matchesValidityDateRange(order: SalesOrder, range: DateRange): boolean {
  if (!range.from && !range.to) return true

  // For validity range, we check if the order's validity period overlaps with the filter range
  // An order matches if: order.startDate <= range.to AND order.endDate >= range.from
  const orderStart = order.startDate ? new Date(order.startDate) : null
  const orderEnd = order.endDate ? new Date(order.endDate) : null

  if (!orderStart && !orderEnd) return false

  if (range.from && orderEnd) {
    const fromDate = new Date(range.from)
    const orderEndOnly = new Date(orderEnd.getFullYear(), orderEnd.getMonth(), orderEnd.getDate())
    const fromDateOnly = new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate())
    if (orderEndOnly < fromDateOnly) return false
  }

  if (range.to && orderStart) {
    const toDate = new Date(range.to)
    const orderStartOnly = new Date(orderStart.getFullYear(), orderStart.getMonth(), orderStart.getDate())
    const toDateOnly = new Date(toDate.getFullYear(), toDate.getMonth(), toDate.getDate())
    if (orderStartOnly > toDateOnly) return false
  }

  return true
}

function applyClientSideFilters(orders: SalesOrder[], filter?: SalesOrdersFilter): SalesOrder[] {
  const productCodes = normalizeProductCodes(filter?.productCodes)
  const deliveryAddresses = normalizeDeliveryAddresses(filter?.deliveryAddresses)
  const factoryIds = normalizeFactoryIds(filter?.factoryIds)
  const valueRange = normalizeValueRange(filter?.minValue, filter?.maxValue)
  const statuses = normalizeStatusFilter(filter?.status)
  const createdDateRange = normalizeDateRange(filter?.createdDateRange)
  const validityDateRange = normalizeDateRange(filter?.validityDateRange)
  // Legacy dateRange - treat as validity date range for backwards compatibility
  const legacyDateRange = normalizeDateRange(filter?.dateRange)

  const hasFilters =
    productCodes.length > 0 ||
    deliveryAddresses.length > 0 ||
    factoryIds.length > 0 ||
    valueRange.min != null ||
    valueRange.max != null ||
    statuses.length > 0 ||
    createdDateRange.from != null ||
    createdDateRange.to != null ||
    validityDateRange.from != null ||
    validityDateRange.to != null ||
    legacyDateRange.from != null ||
    legacyDateRange.to != null

  if (!hasFilters) {
    return orders
  }

  return orders.filter((order) => {
    if (!matchesStatusFilter(order, statuses)) return false
    if (!matchesProductCodes(order, productCodes)) return false
    if (!matchesDeliveryAddress(order, deliveryAddresses)) return false
    if (!matchesFactoryId(order, factoryIds)) return false
    if (!matchesValueRange(order.totalValue.amount, valueRange)) return false
    if (!matchesDateRange(order.createdAt, createdDateRange)) return false
    if (!matchesValidityDateRange(order, validityDateRange)) return false
    // Legacy dateRange as validity filter
    if (!matchesValidityDateRange(order, legacyDateRange)) return false
    return true
  })
}

/**
 * Get sortable value for a sales order based on sort field
 */
function getSortValue(order: SalesOrder, sortBy: SalesOrdersSortBy): number {
  switch (sortBy) {
    case 'totalValue':
      return order.totalValue?.amount ?? 0
    case 'consumedValue':
      // Consumption percentage: quantity-based (not value-based)
      return calculateUtilization(order)
    case 'remainingValue':
      return (order.totalValue?.amount ?? 0) - (order.usedValue?.amount ?? 0)
    case 'productCount':
      return order.lines?.length ?? 0
    case 'ordersCount':
      return order.ordersCount?.total ?? 0
    default:
      return 0
  }
}

/**
 * Apply client-side sorting for fields that require computation
 */
function applyClientSideSorting(orders: SalesOrder[], filter?: SalesOrdersFilter): SalesOrder[] {
  const sortBy = filter?.sortBy
  const sortOrder = filter?.sortOrder ?? 'desc'

  // Only apply client-side sorting for computed fields
  if (!sortBy || !isClientSortField(sortBy)) {
    return orders
  }

  return [...orders].sort((a, b) => {
    const aValue = getSortValue(a, sortBy)
    const bValue = getSortValue(b, sortBy)
    return sortOrder === 'asc' ? aValue - bValue : bValue - aValue
  })
}

function buildPopulateParams(): URLSearchParams {
  const params = new URLSearchParams()

  params.set('populate[salesOrderLines][populate][variant][fields][0]', 'documentId')
  params.set('populate[salesOrderLines][populate][variant][fields][1]', 'name')
  params.set('populate[salesOrderLines][populate][variant][fields][2]', 'locale')
  params.set('populate[salesOrderLines][populate][variant][fields][3]', 'variantCode')
  params.set('populate[salesOrderLines][populate][variant][populate][localizations][fields][0]', 'locale')
  params.set('populate[salesOrderLines][populate][variant][populate][localizations][fields][1]', 'name')
  // salesOrderLines → variant → sku → categories: restrict to code only (transformer uses categories[0].code)
  params.set('populate[salesOrderLines][populate][variant][populate][sku][populate][categories][fields][0]', 'code')

  // tenant: only the 4 fields used in the transformer
  params.set('populate[tenant][fields][0]', 'documentId')
  params.set('populate[tenant][fields][1]', 'name')
  params.set('populate[tenant][fields][2]', 'code')
  params.set('populate[tenant][fields][3]', 'tenantStatus')

  // factory: only the 4 fields used in the transformer
  params.set('populate[factory][fields][0]', 'documentId')
  params.set('populate[factory][fields][1]', 'name')
  params.set('populate[factory][fields][2]', 'code')
  params.set('populate[factory][fields][3]', 'sapPlantCode')

  params.set('populate[city]', 'true')
  params.set('populate[orders][fields][0]', 'id')
  params.set('populate[orders][fields][1]', 'orderStatus')

  // createdByUser: only fields used in the transformer
  params.set('populate[createdByUser][fields][0]', 'id')
  params.set('populate[createdByUser][fields][1]', 'username')
  params.set('populate[createdByUser][fields][2]', 'email')
  params.set('populate[createdByUser][fields][3]', 'fullName')

  return params
}

function normalizeSkuLabel(name: string, variantName?: string): string {
  const safeName = name.trim() || 'SKU'
  if (!variantName) return safeName
  return `${safeName} · ${variantName.trim()}`
}

function normalizeSkuCode(value?: unknown): string | null {
  const raw = String(value ?? '').trim()
  return raw ? raw : null
}

function buildSkuFilterOptions(rawSkus: unknown[]): SalesOrderSkuOption[] {
  const options: SalesOrderSkuOption[] = []
  const seen = new Set<string>()

  for (const sku of rawSkus) {
    const skuRecord = sku as Record<string, unknown>
    const skuName = String(skuRecord?.name ?? skuRecord?.code ?? '').trim()
    const skuCode = normalizeSkuCode(skuRecord?.code)
    const variants = skusApi.extractVariants(sku)

    if (variants.length > 0) {
      for (const variant of variants) {
        // Use variantCode (actual Strapi field) or skuCode (TypeScript alias), fall back to parent SKU code
        const variantCode = normalizeSkuCode(variant.variantCode ?? variant.skuCode) ?? skuCode
        if (!variantCode) continue
        const variantName = String(variant.name ?? '').trim() || undefined
        const variantId = normalizeSkuCode(variant.documentId ?? variant.id ?? undefined)
        const key = `${variantCode}:${variantId ?? 'none'}`
        if (seen.has(key)) continue
        seen.add(key)
        options.push({
          code: variantCode,
          label: normalizeSkuLabel(skuName || variantCode, variantName),
          skuName: skuName || undefined,
          variantName,
          variantId: variantId ?? undefined,
        })
      }
      continue
    }

    if (!skuCode) continue
    const key = `${skuCode}:sku`
    if (seen.has(key)) continue
    seen.add(key)
    options.push({
      code: skuCode,
      label: normalizeSkuLabel(skuName || skuCode),
      skuName: skuName || undefined,
    })
  }

  return options.sort((a, b) => a.label.localeCompare(b.label))
}

function buildListParams(filter?: SalesOrdersFilter): URLSearchParams {
  const params = buildPopulateParams()

  // Pagination
  const page = filter?.page ?? 1
  const pageSize = filter?.pageSize ?? DEFAULT_PAGE_SIZE
  params.set('pagination[page]', String(page))
  params.set('pagination[pageSize]', String(pageSize))

  // Server-side sorting (only for API-sortable fields)
  const sortBy = (filter?.sortBy ?? DEFAULT_SALES_ORDER_SORT.sortBy) as SalesOrdersSortBy
  const sortOrder = filter?.sortOrder ?? DEFAULT_SALES_ORDER_SORT.sortOrder
  const apiSortField = getApiSortField(sortBy)

  // If the field is API-sortable, use server-side sort
  // Otherwise, default to createdAt and we'll apply client-side sort later
  const effectiveSortField = apiSortField ?? 'createdAt'
  params.set('sort[0]', `${effectiveSortField}:${sortOrder}`)

  const statuses = normalizeStatusFilter(filter?.status)
  const apiStatuses = buildApiStatusFilter(statuses)
  if (apiStatuses.length > 0) {
    apiStatuses.forEach((status, index) => {
      params.set(`filters[status][$in][${index}]`, status)
    })
  }

  if (filter?.type && filter.type !== 'all') {
    params.set('filters[metadata][type][$eq]', filter.type)
  }

  if (filter?.search) {
    applySearchFilters(params, filter.search)
  }

  // Factory filter - server-side
  if (filter?.factoryIds && filter.factoryIds.length > 0) {
    filter.factoryIds.forEach((factoryId, index) => {
      params.set(`filters[factory][documentId][$in][${index}]`, factoryId)
    })
  }

  // Product filter - server-side (filter by salesOrderLine variant documentId)
  // This ensures pagination works correctly when filtering by product:
  // without server-side filtering, client-side filter would only apply to the
  // current page, missing matching orders on other pages.
  if (filter?.productCodes && filter.productCodes.length > 0) {
    const isNumericId = (code: string) => /^\d+$/.test(code)
    filter.productCodes.forEach((code, index) => {
      if (isNumericId(code)) {
        // Numeric Strapi ID (legacy fallback)
        params.set(`filters[salesOrderLines][variant][id][$in][${index}]`, code)
      } else {
        // documentId (primary case for Strapi v5)
        params.set(`filters[salesOrderLines][variant][documentId][$in][${index}]`, code)
      }
    })
  }

  return params
}

// ============================================
// ID Resolution
// ============================================

async function resolveDocumentId(numericId: string): Promise<string | null> {
  const http = getHttpClient()
  const params = new URLSearchParams()
  params.set('filters[id][$eq]', numericId)
  params.set('pagination[pageSize]', '1')

  const response = await http.get<unknown>(`${SALES_ORDER_ENDPOINTS.LIST}?${params}`)
  const validated = salesOrdersResponseSchema.parse(response.data)
  const match = validated.data?.[0]
  return match?.documentId ? String(match.documentId) : null
}

async function withResolvedId<T>(id: string, operation: (resolvedId: string) => Promise<T>): Promise<T> {
  try {
    return await operation(id)
  } catch (error) {
    const status = (error as { response?: { status?: number } })?.response?.status
    if (status !== 404 || !isNumericId(id)) throw error

    const documentId = await resolveDocumentId(id)
    if (!documentId) throw error
    return operation(documentId)
  }
}

// ============================================
// PDF Helpers
// ============================================

const PDF_MIME_TYPE = 'application/pdf'

function ensurePdfExtension(filename: string): string {
  const trimmed = filename.trim()
  if (!trimmed) return 'sales-order-orders.pdf'
  return trimmed.toLowerCase().endsWith('.pdf') ? trimmed : `${trimmed}.pdf`
}

function sanitizeFilename(filename: string): string {
  return filename
    .replace(/[\\/:*?"<>|]+/g, '_')
    .replace(/\s+/g, ' ')
    .trim()
}

function extractFilenameFromContentDisposition(contentDisposition?: string): string | null {
  if (!contentDisposition) return null
  const filenameMatch = contentDisposition.match(/filename[^;=\n]*=\s*(?:"([^"]*)"|([^;\n]*))/i)
  if (!filenameMatch) return null
  const raw = filenameMatch[1] || filenameMatch[2]
  if (!raw) return null
  return raw.replace(/^["']|["']$/g, '').trim()
}

function resolvePdfFilename(id: string, orderNumber: string | undefined, headers: Record<string, string> | undefined): string {
  if (orderNumber && orderNumber.trim()) {
    return ensurePdfExtension(sanitizeFilename(orderNumber))
  }

  const headerValue = headers?.['content-disposition'] || headers?.['Content-Disposition']
  const headerFilename = extractFilenameFromContentDisposition(headerValue)
  if (headerFilename) {
    return ensurePdfExtension(sanitizeFilename(headerFilename))
  }

  return ensurePdfExtension(sanitizeFilename(id))
}

function stripBase64Prefix(data: string): string {
  const prefixIndex = data.indexOf(',')
  if (data.startsWith('data:') && prefixIndex !== -1) {
    return data.slice(prefixIndex + 1)
  }
  return data
}

function decodeBase64ToArrayBuffer(data: string): ArrayBuffer {
  const normalized = stripBase64Prefix(data).replace(/\s/g, '')
  const binary = atob(normalized)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes.buffer as ArrayBuffer
}

function createArrayBufferFromView(data: ArrayBufferView): ArrayBuffer {
  const bytes = new Uint8Array(data.byteLength)
  bytes.set(new Uint8Array(data.buffer, data.byteOffset, data.byteLength))
  return bytes.buffer as ArrayBuffer
}

function createPdfBlob(data: unknown): Blob {
  if (data instanceof Blob) return data
  if (data instanceof ArrayBuffer) return new Blob([data], { type: PDF_MIME_TYPE })
  if (ArrayBuffer.isView(data)) return new Blob([createArrayBufferFromView(data)], { type: PDF_MIME_TYPE })
  if (typeof data === 'string') {
    try {
      return new Blob([decodeBase64ToArrayBuffer(data)], { type: PDF_MIME_TYPE })
    } catch {
      return new Blob([data], { type: PDF_MIME_TYPE })
    }
  }
  return new Blob([JSON.stringify(data ?? '')], { type: PDF_MIME_TYPE })
}

function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob)
  triggerDownload(url, filename, url)
}

async function fetchSalesOrderOrdersPdfPayload(id: string, orderNumber?: string): Promise<{ blob: Blob; filename: string }> {
  const http = getHttpClient()

  return withResolvedId(id, async (resolvedId) => {
    const response = await http.get(SALES_ORDER_ENDPOINTS.DOWNLOAD_ORDERS_PDF(resolvedId), {
      responseType: 'blob',
    })
    const headers = (response.headers ?? {}) as Record<string, string>
    const filename = resolvePdfFilename(id, orderNumber, headers)
    const blob = createPdfBlob(response.data)
    return { blob, filename }
  })
}

// ============================================
// API Client
// ============================================

export const salesOrdersApi = {
  /**
   * Get SKU/variant options for filtering.
   * Matches the data used in the Create SO drawer for consistency.
   */
  async getSkuFilterOptions(): Promise<SalesOrderSkuOption[]> {
    try {
      const products = await skusApi.getProductsWithPricing()
      const options: SalesOrderSkuOption[] = []
      const seen = new Set<string>()

      for (const product of products) {
        const skuName = String(product.name ?? product.code ?? '').trim()
        const pricedVariants = skusApi.extractPricedVariants(product)
        
        for (const v of pricedVariants) {
          // Use variantCode (actual Strapi field) or skuCode (TypeScript alias), fall back to numeric id
          const variantCode = String((v as any).variantCode || v.skuCode || v.id || '').trim()
          if (!variantCode) continue
          
          const variantName = String(v.name ?? '').trim() || undefined
          const documentId = typeof (v as any)?.documentId === 'string' ? String((v as any).documentId).trim() : ''
          const strapiId = (v as any)?.id != null ? String((v as any).id).trim() : ''
          const variantId = documentId || strapiId
          
          // Use variantId as dedup key so variants with the same code but different IDs are all shown
          const key = `${variantCode}:${variantId || 'none'}`
          if (seen.has(key)) continue
          seen.add(key)
          
          options.push({
            code: variantCode,
            label: normalizeSkuLabel(skuName || variantCode, variantName),
            skuName: skuName || undefined,
            variantName,
            variantId: variantId || undefined,
          })
        }
      }

      return options.sort((a, b) => a.label.localeCompare(b.label))
    } catch (error) {
      console.error('Failed to fetch SKUs for filters:', error)
      // Fallback to basic SKUs if pricing call fails
      const skus = await skusApi.getSkus()
      return buildSkuFilterOptions(skus)
    }
  },
  /**
   * Get paginated sales orders with pagination metadata
   */
  async getSalesOrdersPaginated(filter?: SalesOrdersFilter): Promise<PaginatedSalesOrders> {
    const http = getHttpClient()
    const params = buildListParams(filter)

    const response = await http.get<unknown>(`${SALES_ORDER_ENDPOINTS.LIST}?${params}`)
    const validated = salesOrdersResponseSchema.parse(response.data)
    let orders = transformSalesOrdersResponseToDomain(validated.data)

    if (filter?.type && filter.type !== 'all') {
      orders = orders.filter(o => o.type === filter.type)
    }
    orders = applyClientSideFilters(orders, filter)
    orders = applyClientSideSorting(orders, filter)

    const pagination: PaginationMeta = validated.meta?.pagination ?? {
      page: filter?.page ?? 1,
      pageSize: filter?.pageSize ?? DEFAULT_PAGE_SIZE,
      pageCount: 1,
      total: orders.length,
    }

    return { data: orders, pagination }
  },

  /**
   * Get sales orders (backwards compatible - returns array only)
   */
  async getSalesOrders(filter?: SalesOrdersFilter): Promise<SalesOrder[]> {
    const result = await salesOrdersApi.getSalesOrdersPaginated(filter)
    return result.data
  },

  async getSalesOrder(id: string): Promise<SalesOrder | null> {
    const http = getHttpClient()

    return withResolvedId(id, async (resolvedId) => {
      const params = buildPopulateParams()
      const response = await http.get<unknown>(`${SALES_ORDER_ENDPOINTS.DETAIL(resolvedId)}?${params}`)
      const validated = salesOrderResponseSchema.parse(response.data)
      return transformSalesOrderResponseToDomain(validated.data)
    })
  },

  async getSalesOrderOrdersPdfPayload(id: string, orderNumber?: string): Promise<{ blob: Blob; filename: string }> {
    return fetchSalesOrderOrdersPdfPayload(id, orderNumber)
  },

  async downloadSalesOrderOrdersPdf(id: string, orderNumber?: string): Promise<void> {
    const payload = await fetchSalesOrderOrdersPdfPayload(id, orderNumber)
    triggerBlobDownload(payload.blob, payload.filename)
  },

  async getSalesOrderStats(filter?: SalesOrdersFilter): Promise<SalesOrderStats> {
    const http = getHttpClient()

    // Only plant (factory) filter affects stats for now.
    const factoryIds = filter?.factoryIds?.filter(Boolean) ?? []
    const params = new URLSearchParams()
    for (const id of factoryIds) {
      params.append('factoryIds', id)
    }

    const query = params.toString()
    const url = query
      ? `${SALES_ORDER_ENDPOINTS.STATS}?${query}`
      : SALES_ORDER_ENDPOINTS.STATS

    const response = await http.get<unknown>(url)
    const validated = salesOrderStatsResponseSchema.parse(response.data)
    return validated.data
  },

  async createSalesOrder(data: CreateSalesOrderRequest): Promise<SalesOrder> {
    const http = getHttpClient()

    if (!data.cityId) {
      throw new Error('cityId is required')
    }

    // deliveryAddress is a relation field (location ID), not a string
    const deliveryAddressId = coerceToNumberId(data.deliveryAddressId) ??
      (data.deliveryAddressId ? Number.parseInt(data.deliveryAddressId, 10) : undefined)
    const cityIdNumeric = coerceToNumberId(data.cityId)
    const cityRelation = cityIdNumeric ?? data.cityId
    // factory is a relation field - in Strapi 5, we can pass either numeric id or documentId string
    // If it's a numeric ID, use it as a number; otherwise pass the documentId string directly
    const factoryIdNumeric = data.factoryId ? coerceToNumberId(data.factoryId) : null
    const factoryRelation = factoryIdNumeric ?? data.factoryId

    const requestData = {
      data: {
        salesOrderNumber: data.salesOrderNumber,
        status: 'draft',
        channel: data.channel || 'ecommerce',
        city: cityRelation,
        ...(deliveryAddressId && { deliveryAddress: deliveryAddressId }),
        ...(factoryRelation && { factory: factoryRelation }),
        metadata: {
          name: data.name,
          type: data.type,
          customerId: data.customerId,
          terms: data.terms,
          notes: data.notes,
          paymentMethod: data.paymentMethod,
          cityId: data.cityId,
          cityName: data.cityName,
          deliveryAddressId: data.deliveryAddressId,
          deliveryAddress: data.deliveryAddress, // Keep as string in metadata for reference
          ...(data.subCustomerDocumentId ? { subCustomerDocumentId: data.subCustomerDocumentId } : {}),
        },
        salesOrderLines: data.lines.map(line => ({
          committedQuantity: line.quantity.value,
          splitBy: line.splitBy,
          variant: coerceToNumberId(line.variantId) ?? line.variantId,
          metadata: {
            ...(line.metadata ?? {}),
            productId: line.productId,
            productName: line.product,
            productCode: line.productCode,
            packaging: line.packaging ?? (line.quantity.unit === 'bags' ? 'bags' : 'bulk'),
            unitPrice: line.unitPrice,
            variantId: line.variantId,
            deliveryOption: line.deliveryOption,
            ...(line.deliveryOption === 'assign' ? {
              assignee: line.assignee,
              resourceId: line.resourceId,
              resourceDocumentId: line.resourceDocumentId,
            } : {}),
            ...(Array.isArray(line.deliveryOptionSplits) && line.deliveryOptionSplits.length > 0
              ? { deliveryOptionSplits: line.deliveryOptionSplits }
              : {}),
          },
        })),
      },
    }

    try {
      const response = await http.post<unknown>(SALES_ORDER_ENDPOINTS.CREATE, requestData)
      const validated = salesOrderResponseSchema.parse(response.data)
      const createdId = validated?.data?.documentId ? String(validated.data.documentId) : String(validated.data.id)

      // Re-fetch the full entity with populated relations (factory, tenant, etc.)
      const full = await salesOrdersApi.getSalesOrder(createdId)
      return full ?? transformSalesOrderResponseToDomain(validated.data)
    } catch (error: any) {
      // Extract error message from Strapi error response
      const errorMessage = error?.response?.data?.error?.message || 
                          error?.message || 
                          'Failed to create sales order'
      throw new Error(errorMessage)
    }
  },

  async updateSalesOrder(id: string, data: Partial<CreateSalesOrderRequest>): Promise<SalesOrder> {
    const http = getHttpClient()

    return withResolvedId(id, async (resolvedId) => {
      const requestData = {
        data: {
          ...(data.salesOrderNumber && { salesOrderNumber: data.salesOrderNumber }),
          ...(data.cityId && {
            city: coerceToNumberId(data.cityId) ?? data.cityId,
          }),
          // Handle deliveryAddress relation field
          ...(data.deliveryAddressId && {
            deliveryAddress: coerceToNumberId(data.deliveryAddressId) ?? Number.parseInt(data.deliveryAddressId, 10),
          }),
          // Handle factory relation field - support both numeric id and documentId string
          ...(data.factoryId && {
            factory: coerceToNumberId(data.factoryId) ?? data.factoryId,
          }),
        },
      }

      const params = buildPopulateParams()
      const response = await http.put<unknown>(`${SALES_ORDER_ENDPOINTS.UPDATE(resolvedId)}?${params}`, requestData)
      const validated = salesOrderResponseSchema.parse(response.data)
      return transformSalesOrderResponseToDomain(validated.data)
    })
  },

  async deleteSalesOrder(id: string): Promise<void> {
    const http = getHttpClient()
    await withResolvedId(id, async (resolvedId) => {
      await http.delete(SALES_ORDER_ENDPOINTS.DELETE(resolvedId))
    })
  },

  async cancelSalesOrder(id: string): Promise<SalesOrder> {
    const http = getHttpClient()

    return withResolvedId(id, async (resolvedId) => {
      const response = await http.put<unknown>(SALES_ORDER_ENDPOINTS.UPDATE(resolvedId), {
        data: {
          status: 'canceled',
        },
      })

      const validated = salesOrderResponseSchema.parse(response.data)
      const updatedId = validated?.data?.documentId ? String(validated.data.documentId) : String(validated.data.id)

      // Fetch the full entity with populated relations (consistent with other mutations)
      const full = await salesOrdersApi.getSalesOrder(updatedId)
      return full ?? transformSalesOrderResponseToDomain(validated.data)
    })
  },

  async duplicateSalesOrder(id: string): Promise<SalesOrder> {
    const http = getHttpClient()

    return withResolvedId(id, async (resolvedId) => {
      const params = buildPopulateParams()
      const detailResponse = await http.get<unknown>(`${SALES_ORDER_ENDPOINTS.DETAIL(resolvedId)}?${params}`)
      const validated = salesOrderResponseSchema.parse(detailResponse.data)
      const original = validated.data

      const suffix = `-COPY-${String(Date.now()).slice(-6)}`
      const salesOrderNumber = `${original.salesOrderNumber || `SO-${original.id}`}${suffix}`.slice(0, 50)

      const createData = {
        data: {
          salesOrderNumber,
          validityEnd: original.validityEnd,
          status: 'draft',
          metadata: original.metadata,
          salesOrderLines: (original.salesOrderLines || []).map(line => {
            const variant = (line as Record<string, unknown>).variant as Record<string, unknown> | undefined
            const variantId = variant?.id
            return {
              committedQuantity: line.committedQuantity,
              splitBy: line.splitBy,
              status: 'active',
              variant: coerceToNumberId(variantId) ?? variantId,
              metadata: line.metadata,
            }
          }),
        },
      }

      const response = await http.post<unknown>(SALES_ORDER_ENDPOINTS.CREATE, createData)
      const createValidated = salesOrderResponseSchema.parse(response.data)
      return transformSalesOrderResponseToDomain(createValidated.data)
    })
  },

  async renewSalesOrder(id: string, newEndDate: string): Promise<SalesOrder> {
    const http = getHttpClient()

    return withResolvedId(id, async (resolvedId) => {
      const requestData = {
        data: {
          validityEnd: newEndDate,
          status: 'active',
        },
      }

      const response = await http.put<unknown>(SALES_ORDER_ENDPOINTS.UPDATE(resolvedId), requestData)
      const validated = salesOrderResponseSchema.parse(response.data)
      return transformSalesOrderResponseToDomain(validated.data)
    })
  },

  /**
   * Export all sales orders matching current filters (no pagination limit)
   */
  async exportAll(request: SalesOrdersExportRequest): Promise<SalesOrdersExportResult> {
    const http = getHttpClient()
    const { filter, email, locale } = request
    const payload: Record<string, unknown> = { email, locale }

    if (filter?.status) {
      const statusArray = Array.isArray(filter.status) ? filter.status : [filter.status]
      const filtered = statusArray.filter(s => s && s !== 'all')
      if (filtered.length === 1) payload.status = filtered[0]
    }
    if (filter?.search) payload.search = filter.search

    const response = await http.post<unknown>(`${SALES_ORDER_ENDPOINTS.LIST}/export`, payload)
    const responseData = response.data as Record<string, unknown>
    const data = responseData?.data as Record<string, unknown> | undefined

    if (data?.mode === 'email') {
      return {
        mode: 'email',
        count: typeof data.count === 'number' ? data.count : 0,
        email: typeof data.email === 'string' && data.email ? data.email : email,
      }
    }

    const raw = Array.isArray(data?.data) ? data.data : []
    return {
      mode: 'download',
      count: typeof data?.count === 'number' ? data.count : raw.length,
      orders: transformSalesOrdersResponseToDomain(raw as Parameters<typeof transformSalesOrdersResponseToDomain>[0]),
    }
  },
}

// Legacy API alias with method name compatibility
export const contractsApi = {
  ...salesOrdersApi,
  // Legacy method aliases
  getContracts: salesOrdersApi.getSalesOrders,
  getContract: salesOrdersApi.getSalesOrder,
  getContractStats: salesOrdersApi.getSalesOrderStats,
  createContract: salesOrdersApi.createSalesOrder,
  updateContract: salesOrdersApi.updateSalesOrder,
  deleteContract: salesOrdersApi.deleteSalesOrder,
  cancelContract: salesOrdersApi.cancelSalesOrder,
  duplicateContract: salesOrdersApi.duplicateSalesOrder,
  renewContract: salesOrdersApi.renewSalesOrder,
}
